import { Bath, Flame, Lock, MapPin, Navigation, Phone, ShowerHead, Sparkles } from "lucide-react";
import { OpenStatus } from "@/components/site/OpenStatus";
import { HoursTable } from "@/components/site/HoursTable";
import { directionsUrl, fullAddress, mapEmbed } from "@/lib/api";

const ICONS = { Restrooms: Bath, Showers: ShowerHead, Lockers: Lock, Sauna: Flame };

export function Amenities({ loc }) {
  return (
    <ul className="grid grid-cols-2 gap-2" data-testid={`amenities-${loc.id}`}>
      {loc.amenities.map((a) => {
        const Icon = ICONS[a] || Sparkles;
        const perk = a === "Sauna";
        return (
          <li key={a} className={`flex items-center gap-2 rounded-sm border px-3 py-2 text-sm ${perk ? "border-[var(--gold)]/50 bg-[var(--gold)]/10 text-[var(--gold)]" : "border-white/10 text-white/80"}`}>
            <Icon className="h-4 w-4 shrink-0" /> {perk ? `Sauna (${loc.name} location)` : a}
          </li>
        );
      })}
    </ul>
  );
}

export function CallDirections({ loc }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <a href={`tel:${loc.phone_tel}`} className="btn-gold !px-3" data-testid={`call-btn-${loc.id}`}><Phone className="h-4 w-4" /> Call</a>
      <a href={directionsUrl(loc)} target="_blank" rel="noopener noreferrer" className="btn-ghost !px-3" data-testid={`directions-btn-${loc.id}`}><Navigation className="h-4 w-4" /> Directions</a>
    </div>
  );
}

export function MapEmbed({ loc, className = "h-64" }) {
  return (
    <iframe title={`Map of Iron Pump Gym ${loc.name}`} src={mapEmbed(loc)} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
      className={`w-full rounded-sm border border-white/10 grayscale-[0.6] invert-[0.9] hue-rotate-180 ${className}`} data-testid={`map-${loc.id}`} />
  );
}

export function LocationCard({ loc }) {
  return (
    <article className="overflow-hidden rounded-md border border-white/10 border-l-4 border-l-[var(--gold)] bg-[#121212]" data-testid={`location-card-${loc.id}`}>
      <div className="relative h-52 overflow-hidden sm:h-64">
        <img src={loc.image} alt={`Iron Pump Gym ${loc.name}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
        <div className="absolute bottom-4 left-5 right-5 flex flex-wrap items-end justify-between gap-3">
          <h3 className="h-display text-5xl text-white">{loc.name}</h3>
          <OpenStatus loc={loc} />
        </div>
      </div>
      <div className="space-y-6 p-5 sm:p-7">
        <div className="space-y-2">
          <p className="flex gap-2 text-white"><MapPin className="mt-1 h-4 w-4 shrink-0 text-[var(--gold)]" /> <span>{fullAddress(loc)}</span></p>
          {loc.note && <p className="ml-6 inline-block rounded-sm bg-[var(--gold)] px-2 py-0.5 font-mono-ui text-[11px] font-bold uppercase tracking-wider text-black" data-testid={`location-note-${loc.id}`}>{loc.note}</p>}
          <a href={`tel:${loc.phone_tel}`} className="flex items-center gap-2 text-white/80 hover:text-[var(--gold)]"><Phone className="h-4 w-4 text-[var(--gold)]" /> {loc.phone}</a>
        </div>
        <Amenities loc={loc} />
        <HoursTable hours={loc.hours} testid={`hours-${loc.id}`} />
        <MapEmbed loc={loc} />
        <CallDirections loc={loc} />
      </div>
    </article>
  );
}
